import { dayNumber, formatDate, type Time, ymd } from "./calendar";
import {
  abs,
  addUnit,
  clock,
  dayOfMonth,
  endOfMonth,
  endOfWeek,
  expandYear,
  firstOfNextMonth,
  monthDay,
  nextWeekday,
  nextWorkdayAfter,
  rel,
  TIME_OF_DAY,
  thisWeekday,
  thisWeekend,
  toTime,
  type Unit,
  upcomingWeekday,
  weekdayInWeek,
} from "./common";
import { rx, Scanner } from "./scanner";
import type { DateRef, Edit, Env, Parsed, Rule } from "./types";

// ---------------------------------------------------------------------------
// Vocabulary

const MONTH =
  "(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\.?";
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const monthOf = (s: string) => MONTHS.indexOf(s.slice(0, 3)) + 1;

const WD =
  "(monday|mon|tuesday|tues|tue|wednesday|weds|wed|thursday|thurs|thur|thu|friday|fri|saturday|sat|sunday|sun)";
const WDS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const wdOf = (s: string) => WDS.indexOf(s.slice(0, 3)) + 1;

const NUM_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};
const NUM = `(\\d{1,4}|${Object.keys(NUM_WORDS)
  .sort((a, b) => b.length - a.length)
  .join("|")})`;
const numOf = (s: string) => NUM_WORDS[s] ?? Number(s);

const ORD_WORDS: Record<string, number> = { first: 1, second: 2, third: 3, fourth: 4, fifth: 5, last: -1 };
const ORD = "(?:(\\d{1,2})(?:st|nd|rd|th)|(first|second|third|fourth|fifth|last))";
const ordOf = (m: RegExpExecArray, i: number) => (m[i] ? Number(m[i]) : (ORD_WORDS[m[i + 1] as string] ?? 0));

const UNIT =
  "(minutes?|mins?|hours?|hrs?|h|days?|d|workdays?|weekdays?|business\\s+days?|weeks?|wks?|w|months?|mos?|years?|yrs?|y|quarters?)";
type EnUnit = Unit | "quarter" | "workday";
function unitOf(s: string): EnUnit {
  if (s.startsWith("min")) return "minute";
  if (s.startsWith("h")) return "hour";
  if (s.startsWith("work") || s.startsWith("weekday") || s.startsWith("business")) return "workday";
  if (s.startsWith("d")) return "day";
  if (s.startsWith("w")) return "week";
  if (s.startsWith("mo")) return "month";
  if (s.startsWith("q")) return "quarter";
  return "year";
}

const SEP = rx("(?:,|and\\b|&)");

// ---------------------------------------------------------------------------
// Times

const MERIDIEM = "(am|pm|a\\.m\\.|p\\.m\\.)";
const meridiemOf = (s: string | undefined) => (s ? (s.startsWith("a") ? "am" : "pm") : undefined);

export function parseTimeEn(sc: Scanner): Time | null {
  return sc.try(() => {
    const at = sc.eat(rx("(?:at|@)")) !== null;
    if (sc.eat(rx("(?:noon|midday)"))) return { h: 12, m: 0 };
    if (sc.eat(rx("midnight"))) return { h: 0, m: 0 };
    let m = sc.eat(rx(`(\\d{1,2}):(\\d{2})(?:\\s*${MERIDIEM})?`));
    if (m) return clock(Number(m[1]), Number(m[2]), meridiemOf(m[3]));
    m = sc.eat(rx(`(\\d{1,2})\\s*${MERIDIEM}`));
    if (m) return clock(Number(m[1]), 0, meridiemOf(m[2]));
    if (at) {
      m = sc.eat(rx("(\\d{1,2})(?![\\d/.:-]|\\s*(?:st|nd|rd|th)\\b)"));
      if (m) return clock(Number(m[1]), 0);
    }
    m = sc.eat(rx("(?:in\\s+the\\s+|this\\s+)?(morning|afternoon|evening|night)"));
    if (m) return TIME_OF_DAY[m[1] as keyof typeof TIME_OF_DAY];
    return null;
  });
}

// ---------------------------------------------------------------------------
// Dates

interface DateHit {
  ref: DateRef;
  /** Set when the expression also fixes a time ("in 2 hours"). */
  minutes?: number;
}

function holiday(sc: Scanner, env: Env): DateRef | null {
  const table: [string, number, number][] = [
    ["new\\s*year'?s?\\s*eve|nye", 12, 31],
    ["new\\s*year'?s?(?:\\s*day)?", 1, 1],
    ["valentine'?s?(?:\\s*day)?", 2, 14],
    ["halloween", 10, 31],
    ["christmas\\s*eve", 12, 24],
    ["christmas(?:\\s*day)?|xmas", 12, 25],
  ];
  for (const [src, m, d] of table) if (sc.eat(rx(`(?:${src})`))) return monthDay(env, m, d);
  return null;
}

/** Month-name or numeric calendar date. */
function calendarDate(sc: Scanner, env: Env): DateRef | null {
  let m = sc.eat(rx("(\\d{4})[-/](\\d{1,2})[-/](\\d{1,2})"));
  if (m) return monthDay(env, Number(m[2]), Number(m[3]), Number(m[1]));
  m = sc.eat(rx("(\\d{1,2})\\.(\\d{1,2})\\.(\\d{2,4})"));
  if (m) return monthDay(env, Number(m[2]), Number(m[1]), expandYear(Number(m[3])));
  m = sc.eat(rx("(\\d{1,2})/(\\d{1,2})(?:/(\\d{2,4}))?"));
  if (m) {
    let mo = Number(m[1]);
    let d = Number(m[2]);
    // en defaults to month/day; a first number above 12 can only be a day.
    if (mo > 12) [mo, d] = [d, mo];
    return monthDay(env, mo, d, m[3] ? expandYear(Number(m[3])) : undefined);
  }
  m = sc.eat(rx(`${MONTH}\\s*(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s*(\\d{4}))?`));
  if (m) return monthDay(env, monthOf(m[1] as string), Number(m[2]), m[3] ? Number(m[3]) : undefined);
  m = sc.eat(rx(`(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:of\\s+)?${MONTH}(?:,?\\s*(\\d{4}))?`));
  if (m) return monthDay(env, monthOf(m[2] as string), Number(m[1]), m[3] ? Number(m[3]) : undefined);
  m = sc.eat(rx(`mid(?:-|\\s+)?${MONTH}`));
  if (m) return monthDay(env, monthOf(m[1] as string), 15);
  m = sc.eat(rx(`end\\s+of\\s+${MONTH}`));
  if (m) {
    const first = monthDay(env, monthOf(m[1] as string), 1);
    return first ? abs(endOfMonth(first.dn)) : null;
  }
  return null;
}

function parseDateEn(sc: Scanner, env: Env): DateHit | null {
  const { today } = env;
  const r = (dn: number | null | undefined): DateHit | null => (dn == null ? null : { ref: rel(dn) });
  const hit = (ref: DateRef | null): DateHit | null => (ref ? { ref } : null);
  return sc.try((): DateHit | null => {
    let m: RegExpExecArray | null;
    if (sc.eat(rx("(?:the\\s+)?day\\s+after\\s+tomorrow"))) return r(today + 2);
    if (sc.eat(rx("(?:today|tod|tdy)"))) return r(today);
    if (sc.eat(rx("(?:tomorrow|tmrw|tmr|tom)"))) return r(today + 1);
    if (sc.eat(rx("yesterday"))) return r(today - 1);
    if (sc.eat(rx("someday"))) return r(addUnit(today, 0, "month", 2).dn);
    if (sc.eat(rx("next\\s+(?:weekday|workday|business\\s+day)"))) return r(nextWorkdayAfter(today));
    if (sc.eat(rx("next\\s+weekend"))) return r(thisWeekend(env) + 7);
    if (sc.eat(rx("(?:this\\s+)?weekend"))) return r(thisWeekend(env));
    if (sc.eat(rx("next\\s+(?:week|wk)"))) return r(weekdayInWeek(env, env.nextWeek, 1));
    if (sc.eat(rx("next\\s+month"))) return r(firstOfNextMonth(today));
    if (sc.eat(rx("next\\s+year"))) return r(dayNumber(ymd(today).y + 1, 1, 1));
    if (sc.eat(rx("later\\s+this\\s+week"))) return r(Math.max(today, Math.min(today + 2, endOfWeek(env))));
    if (sc.eat(rx("(?:end\\s+of\\s+(?:the\\s+)?week|eow|this\\s+week)"))) return r(Math.max(today, endOfWeek(env)));
    if (sc.eat(rx("(?:end\\s+of\\s+(?:the\\s+)?month|eom|this\\s+month)"))) return r(endOfMonth(today));
    if (sc.eat(rx("(?:end\\s+of\\s+(?:the\\s+)?year|eoy|this\\s+year)"))) return r(dayNumber(ymd(today).y, 12, 31));

    // in 3 days / 3 days from now
    m = sc.eat(rx(`in\\s+${NUM}\\s*${UNIT}`)) ?? sc.eat(rx(`${NUM}\\s*${UNIT}\\s+(?:from\\s+now|later)`));
    if (m) {
      const n = numOf(m[1] as string);
      const unit = unitOf(m[2] as string);
      if (unit === "workday") {
        let d = today;
        for (let i = 0; i < n; i++) d = nextWorkdayAfter(d);
        return r(d);
      }
      const u: Unit = unit === "quarter" ? "month" : unit;
      const k = unit === "quarter" ? 3 * n : n;
      const res = addUnit(today, env.nowMinutes, u, k);
      return u === "minute" || u === "hour" ? { ref: rel(res.dn), minutes: res.minutes } : r(res.dn);
    }
    // 50 days before new year / 6 weeks after jul 21
    m = sc.eat(rx(`${NUM}\\s*${UNIT}\\s+(before|after)`));
    if (m) {
      const n = numOf(m[1] as string) * (m[3] === "before" ? -1 : 1);
      const unit = unitOf(m[2] as string);
      if (unit === "minute" || unit === "hour" || unit === "workday") return null;
      const base = parseDateEn(sc, env);
      if (!base || base.minutes !== undefined) return null;
      const u: Unit = unit === "quarter" ? "month" : unit;
      return r(addUnit(base.ref.dn, 0, u, unit === "quarter" ? 3 * n : n).dn);
    }
    m = sc.eat(rx(`(next|this|coming)?\\s*${WD}`));
    if (m) {
      const wd = wdOf(m[2] as string);
      if (m[1] === "next") return r(nextWeekday(env, wd));
      if (m[1] === "this") return r(thisWeekday(env, wd));
      return r(upcomingWeekday(env, wd));
    }
    const cal = calendarDate(sc, env);
    if (cal) return hit(cal);
    const h = holiday(sc, env);
    if (h) return hit(h);
    m = sc.eat(rx("(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)"));
    if (m) return hit(dayOfMonth(env, Number(m[1])));
    return null;
  });
}

// ---------------------------------------------------------------------------
// Non-recurring

function parseOnce(sc: Scanner, env: Env): Parsed | null {
  let date: DateHit | undefined;
  let time: Time | undefined;
  while (!sc.done()) {
    if (sc.eat(rx("(?:,|on|due|by)"))) continue;
    if (!date && !time && sc.eat(rx("tonight"))) {
      date = { ref: rel(env.today) };
      time = TIME_OF_DAY.evening;
      continue;
    }
    if (!time) {
      const t = parseTimeEn(sc);
      if (t) {
        time = t;
        continue;
      }
    }
    if (!date) {
      const d = parseDateEn(sc, env);
      if (d) {
        date = d;
        continue;
      }
    }
    return null;
  }
  if (date?.minutes !== undefined) return time ? null : { kind: "once", dn: date.ref.dn, time: toTime(date.minutes) };
  if (date) return { kind: "once", dn: date.ref.dn, time };
  if (time) {
    // A bare time is today, or tomorrow once it has passed.
    const passed = time.h * 60 + time.m <= env.nowMinutes;
    return { kind: "once", dn: env.today + (passed ? 1 : 0), time };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Recurring

function weekdayList(sc: Scanner): number[] | null {
  const out: number[] = [];
  for (;;) {
    const m = sc.eat(rx(WD));
    if (!m) break;
    out.push(wdOf(m[1] as string));
    const save = sc.pos;
    if (!sc.eat(SEP)) break;
    if (!sc.try(() => (sc.eat(rx(WD)) ? true : null))) {
      sc.pos = save;
      break;
    }
    sc.pos = save;
    sc.eat(SEP);
  }
  return out.length ? [...new Set(out)] : null;
}

/** Ordinal items: "2nd monday", "last day", "15th", "first workday", "2, 15, 27". */
function ordinalItems(sc: Scanner, rule: Rule): boolean {
  const days: number[] = [];
  const nth: { n: number; wd: number }[] = [];
  const work: number[] = [];
  let count = 0;
  for (;;) {
    const item = sc.try(() => {
      let m = sc.eat(rx(ORD));
      let n: number;
      if (m) n = ordOf(m, 1);
      else {
        m = sc.eat(rx(`(\\d{1,2})(?!\\s*${UNIT.replace("(", "(?:")}\\b)(?![:/.\\d])`));
        if (!m) return null;
        n = Number(m[1]);
      }
      if (n === 0 || n > 31) return null;
      const wd = sc.eat(rx(WD));
      if (wd) {
        if (n > 5) return null;
        nth.push({ n, wd: wdOf(wd[1] as string) });
      } else if (sc.eat(rx("(?:workday|weekday|business\\s+day)"))) work.push(n);
      else {
        sc.eat(rx("day"));
        days.push(n);
      }
      return true;
    });
    if (!item) break;
    count++;
    const save = sc.pos;
    if (!sc.eat(SEP)) break;
    if (!sc.try(() => (sc.eat(rx(`(?:${ORD}|\\d{1,2}(?![:/.\\d]))`)) ? true : null))) {
      sc.pos = save;
      break;
    }
    sc.pos = save;
    sc.eat(SEP);
  }
  if (!count) return false;
  const ofMonth = sc.eat(rx(`of\\s+${MONTH}`));
  if (ofMonth) {
    if (days.length || work.length) {
      const mo = monthOf(ofMonth[1] as string);
      if (work.length) return false;
      rule.freq = "year";
      rule.yearDates = days.map((d) => ({ m: mo, d }));
      return true;
    }
    rule.freq = "year";
    rule.yearNth = nth.map((x) => ({ ...x, m: monthOf(ofMonth[1] as string) }));
    return true;
  }
  sc.eat(rx("of\\s+(?:the\\s+|every\\s+|each\\s+)?month"));
  rule.freq = "month";
  if (days.length) rule.monthDays = days;
  if (nth.length) rule.nthWeekdays = nth;
  if (work.length) rule.nthWorkdays = work;
  return true;
}

function yearDateList(sc: Scanner): { m: number; d: number }[] | null {
  const out: { m: number; d: number }[] = [];
  for (;;) {
    const item = sc.try(() => {
      let m = sc.eat(rx(`${MONTH}\\s*(\\d{1,2})(?:st|nd|rd|th)?(?![:\\d])`));
      if (m) return { m: monthOf(m[1] as string), d: Number(m[2]) };
      m = sc.eat(rx(`(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:of\\s+)?${MONTH}`));
      if (m) return { m: monthOf(m[2] as string), d: Number(m[1]) };
      return null;
    });
    if (!item || !monthDay({ today: 0, nowMinutes: 0, startDay: 1, nextWeek: 1, weekendStartDay: 6 }, item.m, item.d))
      break;
    out.push(item);
    const save = sc.pos;
    if (!sc.eat(SEP)) break;
    const more = sc.try(() =>
      sc.eat(rx(`(?:${MONTH}|\\d{1,2}(?:st|nd|rd|th)?\\s*(?:of\\s+)?${MONTH})`)) ? true : null,
    );
    sc.pos = save;
    if (!more) break;
    sc.eat(SEP);
  }
  return out.length ? out : null;
}

function parseRecurringEn(sc: Scanner, env: Env): Parsed | null {
  const rule: Rule = { freq: "day", interval: 1, fromCompletion: false };
  let time: Time | undefined;
  const edits: Edit[] = [];

  const simple = sc.eat(rx("(everyday|daily|weekly|monthly|yearly|annually|quarterly|hourly)"));
  if (simple) {
    const w = simple[1] as string;
    if (w === "weekly") rule.freq = "week";
    else if (w === "monthly") rule.freq = "month";
    else if (w === "yearly" || w === "annually") rule.freq = "year";
    else if (w === "quarterly") {
      rule.freq = "month";
      rule.interval = 3;
    } else if (w === "hourly") rule.freq = "hour";
  } else {
    const head = sc.eat(rx("(every\\s*!|ev\\s*!|after|every|each|ev)"));
    if (!head) return null;
    const h = head[1] as string;
    if (h.includes("!") || h === "after") rule.fromCompletion = true;
    if (h === "after") edits.push({ from: sc.pos - head[0].length, to: sc.pos, text: "every!" });

    let interval: number | undefined;
    if (sc.eat(rx("other"))) interval = 2;
    else {
      const n = sc.try(() => {
        const m = sc.eat(rx(NUM));
        if (!m) return null;
        // Only an interval when a unit follows ("every 3 days"); "every 2, 15" is a list of days.
        const save = sc.pos;
        const u = sc.eat(rx(UNIT));
        sc.pos = save;
        return u ? numOf(m[1] as string) : null;
      });
      if (n !== null) {
        if (n < 1) return null;
        interval = n;
      }
    }
    if (h === "after" && interval === undefined) {
      // "after N days" needs a count.
      const u = sc.try(() => sc.eat(rx(UNIT)));
      if (!u) return null;
      sc.pos -= u[0].length;
    }

    const unit = sc.eat(rx(`(?:(weekends?)|${UNIT})`));
    const tod = !unit && interval === undefined ? sc.eat(rx("(morning|afternoon|evening|night)")) : null;
    if (unit) {
      if (unit[1]) {
        if (interval !== undefined && interval !== 1) {
          rule.freq = "week";
          rule.interval = interval;
        } else rule.freq = "week";
        rule.weekdays = [env.weekendStartDay, (env.weekendStartDay % 7) + 1];
      } else {
        const u = unitOf(unit[2] as string);
        if (u === "minute") return null;
        if (u === "workday") {
          rule.freq = "day";
          rule.workdays = true;
        } else if (u === "quarter") {
          rule.freq = "month";
          rule.interval = 3;
        } else rule.freq = u;
        if (interval !== undefined) rule.interval *= interval;
        if (rule.freq === "week" && sc.eat(rx("on"))) {
          const wds = weekdayList(sc);
          if (!wds) return null;
          rule.weekdays = wds;
        }
      }
    } else if (tod) {
      rule.freq = "day";
      time = TIME_OF_DAY[tod[1] as keyof typeof TIME_OF_DAY];
    } else {
      const wds = weekdayList(sc);
      if (wds) {
        rule.freq = "week";
        rule.weekdays = wds;
      } else {
        const ydates = yearDateList(sc);
        if (ydates) {
          rule.freq = "year";
          rule.yearDates = ydates;
        } else if (!ordinalItems(sc, rule)) return null;
      }
      if (interval !== undefined) rule.interval = interval;
    }
  }

  // Modifiers: time, start, end — in any order.
  let start: DateRef | undefined;
  let startTime: Time | undefined;
  let end: DateRef | undefined;
  while (!sc.done()) {
    if (sc.eat(rx(","))) continue;
    if (!time) {
      const t = parseTimeEn(sc);
      if (t) {
        time = t;
        continue;
      }
    }
    if (!start && sc.eat(rx("(?:starting|starts?|from|beginning|begins?)(?:\\s+(?:on|at|from))?"))) {
      const d = parseDateEn(sc, env);
      const t = parseTimeEn(sc);
      if (!d && !t) return null;
      if (d) {
        if (d.minutes !== undefined) return null;
        start = d.ref;
      }
      if (t) startTime = t;
      continue;
    }
    if (!end && sc.eat(rx("(?:until|till|til|ending|ends?|to|through|thru)(?:\\s+on)?"))) {
      sc.ws();
      const from = sc.pos;
      const d = parseDateEn(sc, env);
      if (!d || d.minutes !== undefined) return null;
      end = d.ref;
      if (!end.absolute) edits.push({ from, to: sc.pos, text: formatDate(end.dn) });
      continue;
    }
    if (!end) {
      const from = sc.pos;
      const m = sc.eat(rx(`for\\s+${NUM}\\s*${UNIT}`));
      if (m) {
        const u = unitOf(m[2] as string);
        if (u === "minute" || u === "hour" || u === "workday") return null;
        const n = numOf(m[1] as string) * (u === "quarter" ? 3 : 1);
        const base = start?.dn ?? env.today;
        end = rel(addUnit(base, 0, u === "quarter" ? "month" : u, n).dn - 1);
        edits.push({ from, to: sc.pos, text: `until ${formatDate(end.dn)}` });
        continue;
      }
    }
    return null;
  }
  return { kind: "recur", rule, time, start, startTime, end, edits };
}

const RECURRING_HEAD = rx(
  "\\s*(?:every|each|ev\\b|ev\\s*!|after\\s+(?:\\d|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|an?\\b)|everyday|daily|weekly|monthly|yearly|annually|quarterly|hourly)",
);

/** Parses a lowercased, whitespace-normalized English due string. */
export function parseEn(s: string, env: Env): Parsed | null {
  if (/^(?:no\s+(?:due\s+)?date|none|no\s+due)$/.test(s)) return { kind: "clear" };
  RECURRING_HEAD.lastIndex = 0;
  if (RECURRING_HEAD.test(s)) {
    const r = parseRecurringEn(new Scanner(s), env);
    if (r) return r;
  }
  return parseOnce(new Scanner(s), env);
}
